import { Container, IntervalAction, SceneObject, Tuple } from '@cgs/wge';
import { ComponentIndex } from '../../../reels_engine/entities_engine/component_index';
import { Entity } from '../../../reels_engine/entities_engine/entity';
import { ComponentNames } from '../../../reels_engine/entity_components/component_names';
import { ISlotGameEngineProvider } from '../../../reels_engine/game_components_providers/i_slot_game_engine_provider';
import { ReelsEngine } from '../../../reels_engine/reels_engine';
import { T_ISlotGameEngineProvider } from '../../../type_definitions';
import { GameComponentProvider } from '../game_component_provider';

export const ICON_DROP_ANIMATION_COMPONENT_TOKEN = Symbol('ICON_DROP_ANIMATION_COMPONENT_TOKEN');

const SECONDS_TO_MS = 1000;

export class IconDropAnimationComponent extends GameComponentProvider {
  protected readonly _engine: ReelsEngine;
  protected readonly _animationStateName: string;
  private readonly _allowedIconIds: Set<number> | null;
  /** Счётчик запусков анимации на ноде — чтобы отложенный стоп не убил более новый запуск. */
  private readonly _animationRunIds = new WeakMap<SceneObject, number>();

  constructor(container: Container, animationStateName: string, allowedIconIds?: number[]) {
    super();
    this._animationStateName = animationStateName;
    this._allowedIconIds = allowedIconIds ? new Set(allowedIconIds) : null;

    this._engine = container.resolve<ISlotGameEngineProvider>(T_ISlotGameEngineProvider)
      .reelsEngine as ReelsEngine;

    this._engine.entityStoped.listen((t: Tuple<number, number>) =>
      this._onEntityStopped(t.item1, t.item2)
    );
  }

  private _onEntityStopped(reel: number, line: number): void {
    let entities;
    try {
      entities = this._engine.getReelStoppedEntities(reel, line, true);
    } catch {
      return;
    }
    if (!entities?.length) return;

    const drawableIndex = this._engine.entityEngine.getComponentIndex(ComponentNames.DrawableIndex);
    const iconNodeIndex = this._engine.entityEngine.getComponentIndex(ComponentNames.IconNode);

    for (const entity of entities) {
      const iconId = entity.get<number>(drawableIndex);
      if (this._allowedIconIds && !this._allowedIconIds.has(iconId)) continue;

      const iconNode = entity.get<SceneObject>(iconNodeIndex);
      if (!iconNode?.stateMachine?.findById(this._animationStateName)) continue;

      this._engine.startAnimation(entity, this._animationStateName);
      this._scheduleAnimationStop(entity, iconNode, iconNodeIndex);
    }
  }

  /**
   * Останавливает анимацию по окончании. Без этого startAnimation оставляет
   * на сущности компонент DrawAnimation, иконка навсегда уходит в anim-слой
   * рендера (над затемнением fade_static_icons) и перестаёт накрываться
   * fade-ом при анимациях выигрыша/скаттеров.
   */
  private _scheduleAnimationStop(
    entity: Entity,
    iconNode: SceneObject,
    iconNodeIndex: ComponentIndex
  ): void {
    const runId = (this._animationRunIds.get(iconNode) ?? 0) + 1;
    this._animationRunIds.set(iconNode, runId);

    const enterAction = iconNode.stateMachine?.findById(this._animationStateName)
      ?.enterAction as IntervalAction;
    const durationMs = (enterAction?.duration ?? 0) * SECONDS_TO_MS;
    if (durationMs <= 0) {
      return;
    }

    setTimeout(() => {
      // Ничего не трогаем, если на ноде уже более новый запуск, нода ушла
      // другой сущности или иконку успели переключить в другое состояние.
      if (this._animationRunIds.get(iconNode) !== runId) return;
      if (entity.get<SceneObject>(iconNodeIndex) !== iconNode) return;
      if (!iconNode.stateMachine?.isActive(this._animationStateName)) return;

      this._engine.stopAnimation(entity, this._animationStateName);
    }, durationMs);
  }
}
